import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import { observer } from "mobx-react-lite";
import L from "leaflet";
import { removeMapLayersById } from "../util/removeMapLayersById";
import { useStores } from "stores/setup/use-store";

interface RadarTileLayerProps {
  url?: string; // Legacy prop for backward compatibility
  minZoom?: number;
  maxZoom?: number;
  opacity?: number;
  zIndex?: number;
  showRadar?: boolean; // Legacy prop, will use store value
}

/**
 * Rainbow AI Radar Tile Layer Component
 *
 * Renders radar tiles from CloudFront CDN with fade-in transitions.
 * Automatically uses currentTileUrl from RadarMapStore (T018).
 * Implements opacity fade-in effect from 0.01 to 0.7 (T019).
 * Conditionally renders based on showRadar and currentTileUrl (T020).
 * Tile URLs include color=0&coverage=1 query params (T021).
 */
const RadarTileLayer: React.FC<RadarTileLayerProps> = observer(
  ({
    url: legacyUrl, // Keep for backward compatibility but prefer store value
    minZoom = 3,
    maxZoom = 7,
    opacity = 0.7,
    zIndex = 900,
    showRadar: legacyShowRadar,
  }) => {
    const map = useMap();
    const [currentLayer, setCurrentLayer] = useState<L.TileLayer | null>(null);
    const isLoadingRef = useRef(false);
    const [isLoadingActionCall, setIsLoadingActionCall] = useState(false);

    // T018: Use currentTileUrl from RadarMapStore
    const {
      dataStores: { radarMapStore },
    } = useStores();

    // Prefer store values over legacy props
    const tileUrl = radarMapStore.currentTileUrl || legacyUrl;
    const showRadar = radarMapStore.showRadar ?? legacyShowRadar;

    const trackTileLoading = (layer: L.TileLayer, onComplete: () => void) => {
      let loadingTiles = 0;
      let loadedTiles = 0;

      const startLoading = () => {
        loadingTiles++;
      };

      const finishLoading = () => {
        setTimeout(() => {
          loadedTiles++;
          if (loadedTiles >= loadingTiles) {
            onComplete();
          }
        }, 250);
      };

      layer.on("loading", startLoading);
      layer.on("load", finishLoading);
    };

    useEffect(() => {
      if (!map) return;

      return () => {
        removeMapLayersById(map, ["radarTileLayer"]);
      };
    }, [map]);

    useEffect(() => {
      // T020: Conditional rendering - hide layer when showRadar is false or no tile URL
      if (!map || !showRadar || !tileUrl) {
        // Remove layers if radar is hidden
        if (currentLayer) {
          map.removeLayer(currentLayer);
          setCurrentLayer(null);
        }
        return;
      }

      if (isLoadingRef.current) {
        setIsLoadingActionCall(!isLoadingActionCall);
        return;
      }
      isLoadingRef.current = true;

      // T018: Use tileUrl from store (includes T021: color=0&coverage=1 params)
      const newLayer = L.tileLayer(tileUrl, {
        minZoom,
        maxZoom,
        opacity: 0.01, // T019: Start with very low opacity
        zIndex,
        id: "radarTileLayer",
      });

      // T019: Fade-in effect - transition from 0.01 to target opacity
      trackTileLoading(newLayer, () => {
        newLayer.setOpacity(opacity); // Fade to 0.7 (default)
        if (currentLayer) {
          map.removeLayer(currentLayer);
        }
        isLoadingRef.current = false;
      });

      newLayer.addTo(map);

      setCurrentLayer(newLayer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      tileUrl,
      showRadar,
      isLoadingActionCall,
      map,
      minZoom,
      maxZoom,
      opacity,
      zIndex,
    ]);
    // Note: currentLayer intentionally excluded from deps to prevent infinite loop

    return null;
  }
);

export default RadarTileLayer;
